import { email, helpers, maxLength, minValue, required } from '@vuelidate/validators'
import { DELIVERY_TYPE } from 'act-components'
import { type zPerson, EMOJI_REGEX } from 'shared-types'
import {
  birthDate,
  birthDateOrHasSsn,
  intMobile,
  isValidDate,
  postcodeDependingOnCountry,
  ssnValidOrHasBirthDate,
  swedishOrInternationalMobile,
} from 'vuelidate-validators'
import { z } from 'zod'

export const commonRules = ({
  t,
  siteLang,
  person,
  minSum,
  currency = 'kr',
  includeCity = false,
  mobileRequired = false,
}: {
  t: Function
  siteLang: string
  person?: z.infer<typeof zPerson>
  minSum?: number
  currency?: string
  includeCity?: boolean
  mobileRequired?: boolean
}) => {
  const rules = {
    sum: {
      minValue: helpers.withMessage(
        t('sum.validation.minimum', { sum: minSum, currency }),
        minValue(minSum || 0),
      ),
    },
    personDetails: {
      ssn: {
        ssnValidOrHasBirthDate: helpers.withMessage(
          t('ssn.swedish.11.validation'),
          ssnValidOrHasBirthDate,
        ),
      },
      birthDate: {
        birthDateOrHasSsn: helpers.withMessage(t('birthDate.validation'), birthDateOrHasSsn),
      },
      contacts: {
        mobile: {
          ...(mobileRequired
            ? {
                required: helpers.withMessage(t('mobile.validation.required'), required),
              }
            : {}),
          mobile: helpers.withMessage(
            t('mobile.validation'),
            siteLang === 'sv' ? swedishOrInternationalMobile : intMobile,
          ),
        },
        email: {
          required: helpers.withMessage(t('email.validation.required'), required),
          email: helpers.withMessage(t('email.validation'), email),
        },
      },
    },
    personPostal: {
      ssn: {
        ssnValidOrHasBirthDate: helpers.withMessage(
          t('ssn.swedish.11.validation'),
          ssnValidOrHasBirthDate,
        ),
      },
      birthDate: {
        birthDateOrHasSsn: helpers.withMessage(t('birthDate.validation'), birthDateOrHasSsn),
      },
      contacts: {
        mobile: {
          mobile: helpers.withMessage(
            t('mobile.validation'),
            siteLang === 'sv' ? swedishOrInternationalMobile : intMobile,
          ),
        },
        email: {
          required: helpers.withMessage(t('email.validation.required'), required),
          email: helpers.withMessage(t('email.validation'), email),
        },
      },
      // only validate name and address if we're not doing a lookup
      ...(!person?.useLookup
        ? {
            name: {
              first: {
                required: helpers.withMessage(
                  t('postal-address.firstname.validation.required'),
                  required,
                ),
              },
              last: {
                required: helpers.withMessage(
                  t('postal-address.lastname.validation.required'),
                  required,
                ),
              },
            },
            address: {
              postcode: {
                required: {
                  ...(!person?.address?.postcode
                    ? helpers.withMessage(
                        t('postal-address.postcode.validation.required'),
                        required,
                      )
                    : undefined),
                },
                postcodeDependingOnCountry: {
                  ...(person?.address?.postcode
                    ? helpers.withMessage(
                        t('postal-address.postcode.validation.swedish.required'),
                        postcodeDependingOnCountry,
                      )
                    : undefined),
                },
              },
              street: {
                required: helpers.withMessage(
                  t('postal-address.street-address.required'),
                  required,
                ),
              },
              ...(includeCity
                ? {
                    city: {
                      required: helpers.withMessage(t('postal-address.city.required'), required),
                    },
                  }
                : {}),
              countryCode: {
                required: helpers.withMessage(
                  t('postal-address.select-country.validation.required'),
                  required,
                ),
              },
            },
          }
        : {
            name: {
              first: {},
              last: {},
            },
            address: {
              postcode: {},
              street: {},
              countryCode: {},
            },
          }),
      useLookup: {}, // just need this to exist on the model for requireLookupOrValue
    },
  }

  return rules
}

export const recipientRules = ({
  t,
  sameAddress,
  person,
}: {
  t: Function
  sameAddress: boolean
  person: z.infer<typeof zPerson>
}) => {
  const rules = {
    recipientDetails: {
      name: {
        first: {
          required: helpers.withMessage(
            t('postal-address.firstname.validation.required'),
            required,
          ),
        },
        last: {
          required: helpers.withMessage(t('postal-address.lastname.validation.required'), required),
        },
      },
      birthDate: {
        birthDate: helpers.withMessage(t('birthDate.validation'), birthDate),
        required: true,
      },
      ...(!sameAddress
        ? {
            address: {
              postcode: {
                required: helpers.withMessage(
                  t('postal-address.postcode.validation.required'),
                  required,
                ),
                postcodeDependingOnCountry: person?.address?.postcode
                  ? helpers.withMessage(
                      t('postal-address.postcode.validation.swedish.required'),
                      postcodeDependingOnCountry,
                    )
                  : {},
              },
              street: {
                required: helpers.withMessage(
                  t('postal-address.street-address.required'),
                  required,
                ),
              },
            },
          }
        : {
            address: {
              postcode: {},
              street: {},
            },
          }),
    },
  }

  return rules
}

export const presentRules = ({
  t,
  handedOut,
  skipGift,
}: {
  t: Function
  handedOut: boolean
  skipGift: boolean
}) => {
  const rules = {
    gift: {
      ...(!handedOut && !skipGift
        ? { required: helpers.withMessage(t('present.validation.required'), required) }
        : {}),
    },
  }
  return rules
}

export const giftcertRules = ({
  t,
  skipGift,
  delivery,
  recipient,
}: {
  t: Function
  skipGift: boolean
  delivery: any
  recipient?: any
}) => {
  const rules = {
    giftCert: {
      id: {
        ...(!skipGift
          ? { required: helpers.withMessage(t('delivery.motif-required'), required) }
          : {}),
      },
      message: {
        // TODO make this dependent on setting in contentful
        required: helpers.withMessage(t('delivery.message-required'), required),
        maxLength: maxLength(300),
        noEmojis: helpers.withMessage(
          t('delivery.message-no-emojis'),
          // Rejects strings containing emoji characters
          (value: string) => !EMOJI_REGEX.test(value),
        ),
      },
      title: {
        required: helpers.withMessage(t('delivery.recipient-required'), required),
      },
    },

    delivery: {
      ...(delivery.value.deliveryType === DELIVERY_TYPE.DIGITAL
        ? {
            email: {
              required: helpers.withMessage(t('email.validation.required'), required),
              email: helpers.withMessage(t('email.validation'), email),
            },
          }
        : {}),

      ...(delivery.value.deliveryType === DELIVERY_TYPE.PRINTED && !delivery.value.mailToYou
        ? {
            name: {
              first: {
                required: helpers.withMessage(
                  t('postal-address.firstname.validation.required'),
                  required,
                ), // TODO: translate
              },
              last: {
                required: helpers.withMessage(
                  t('postal-address.lastname.validation.required'),
                  required,
                ), // TODO: translate
              },
            },
            address: {
              postcode: {
                required: helpers.withMessage(
                  t('postal-address.postcode.validation.required'),
                  required,
                ),
                postcodeDependingOnCountry: recipient?.address?.postcode
                  ? helpers.withMessage(
                      t('postal-address.postcode.validation.swedish.required'),
                      postcodeDependingOnCountry,
                    )
                  : {},
              },
              street: {
                required: helpers.withMessage(
                  t('postal-address.street-address.required'),
                  required,
                ),
              },
            },
            delivery: {
              ...(delivery.value.delivery.when === 'day'
                ? {
                    date: {
                      required: helpers.withMessage(t('delivery.printed2.date.required'), required),
                      isValidDate: helpers.withMessage(t('date.validation'), isValidDate),
                    },
                  }
                : {}),
            },
          }
        : {
            name: {
              first: {},
              last: {},
            },
            address: {
              postcode: {},
              street: {},
            },
          }),
    },
  }

  return rules
}

export const giftedMembershipRules = ({ t }: { t: Function }) => {
  const rules = {
    membership: {
      required: helpers.withMessage(t('membership.validation.required'), required),
    },
  }

  return rules
}

export const companyRules = ({
  t,
  siteLang,
  minSum,
  currency = 'kr',
  includeCity = false,
}: {
  t: Function
  siteLang: string
  minSum?: number
  currency?: string
  includeCity?: boolean
}) => {
  const rules = {
    sum: {
      minValue: helpers.withMessage(
        t('sum.validation.minimum', { sum: minSum, currency }),
        minValue(minSum || 0),
      ),
    },
    companyDetails: {
      orgNumber: {
        required: helpers.withMessage(
          t('company-details.validation.company-number.required'),
          required,
        ),
      },
      phone: {
        phone: helpers.withMessage(
          t('mobile.validation'),
          siteLang === 'sv' ? swedishOrInternationalMobile : intMobile,
        ),
      },
      email: {
        required: helpers.withMessage(t('email.validation.required'), required),
        email: helpers.withMessage(t('email.validation'), email),
      },
    },
    companyPostal: {
      companyName: {
        required: helpers.withMessage(
          t('company-postal-address.name.validation.required'),
          required,
        ),
      },
      address: {
        postcode: {
          required: helpers.withMessage(t('postal-address.postcode.validation.required'), required),
        },
        ...(includeCity
          ? {
              city: {
                required: helpers.withMessage(t('postal-address.city.required'), required),
              },
            }
          : {}),
        street: {
          required: helpers.withMessage(t('postal-address.street-address.required'), required),
        },
        countryCode: {
          required: helpers.withMessage(
            t('postal-address.select-country.validation.required'),
            required,
          ),
        },
      },
    },
  }

  return rules
}
